import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const API = '/admin/diploma-results';

const ATTEMPT_OPTIONS = [
  { value: '1st attempt', label: '1st Attempt' },
  { value: '2nd attempt', label: '2nd Attempt' },
  { value: '3rd attempt', label: '3rd Attempt' },
];

function formatDatetimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Human-friendly display used in the Excel sheet (e.g. "27/04/2026, 01:07 AM")
function formatDatetimeFriendly(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

// ─── Excel Upload Preview Modal ──────────────────────────────────────────────

function ExcelUploadPreviewModal({ open, rows, onClose, onConfirm, submitting }) {
  const [showAll, setShowAll] = useState(false);

  useEffect(() => { if (open) setShowAll(false); }, [open]);

  if (!open) return null;

  const changedRows = rows.filter((r) => r.status !== 'no-change');
  const displayRows = showAll ? rows : changedRows;
  const newCount = rows.filter((r) => r.status === 'new').length;
  const updateCount = rows.filter((r) => r.status === 'update').length;
  const noChangeCount = rows.filter((r) => r.status === 'no-change').length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-800">Upload Preview</h3>
            <p className="text-xs text-gray-500 mt-0.5">Review all changes before confirming</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {/* Summary bar */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium border border-green-200">
            {newCount} new
          </span>
          <span className="text-xs px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-medium border border-blue-200">
            {updateCount} update{updateCount !== 1 ? 's' : ''}
          </span>
          <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 font-medium border border-gray-200">
            {noChangeCount} no change
          </span>
          {rows.length > 0 && (
            <button
              type="button"
              onClick={() => setShowAll((p) => !p)}
              className="ml-auto text-xs text-indigo-600 hover:underline font-medium"
            >
              {showAll ? 'Show changes only' : `Show all ${rows.length} rows`}
            </button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-auto flex-1">
          {displayRows.length === 0 ? (
            <div className="py-16 text-center text-gray-400 text-sm">
              {changedRows.length === 0
                ? 'No changes detected in the uploaded file.'
                : 'All rows are hidden. Click "Show all rows" to view.'}
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Student</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Phase</th>
                  <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Marks</th>
                  <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Attempt</th>
                  <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {displayRows.map((r, i) => (
                  <tr
                    key={`${r.studentId}_${r.phaseId}`}
                    className={r.status === 'no-change' ? 'opacity-40' : ''}
                  >
                    <td className="px-4 py-2.5 text-gray-400 text-xs">{i + 1}</td>
                    <td className="px-4 py-2.5">
                      <div className="text-xs font-medium text-gray-800 whitespace-nowrap">{r.studentName}</div>
                      <div className="text-xs text-gray-400 font-mono">{r.studentId}</div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{r.phaseName}</td>
                    {/* Marks */}
                    <td className="px-4 py-2.5 text-center whitespace-nowrap">
                      {r.status === 'update' && r.currentMarks !== r.newMarks ? (
                        <span className="text-xs">
                          <span className="text-gray-400 line-through mr-1">{r.currentMarks ?? '—'}</span>
                          <span className="text-blue-700 font-semibold">{r.newMarks ?? '—'}</span>
                        </span>
                      ) : (
                        <span className={`text-xs font-semibold ${r.status === 'new' ? 'text-green-700' : 'text-gray-600'}`}>
                          {r.newMarks ?? '—'}
                        </span>
                      )}
                    </td>
                    {/* Attempt */}
                    <td className="px-4 py-2.5 text-center whitespace-nowrap">
                      {r.status === 'update' && r.currentAttempt !== r.newAttempt ? (
                        <span className="text-xs">
                          <span className="text-gray-400 line-through mr-1">{r.currentAttempt ?? '—'}</span>
                          <span className="text-blue-700 font-semibold">{r.newAttempt}</span>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-600">{r.newAttempt}</span>
                      )}
                    </td>
                    {/* Status badge */}
                    <td className="px-4 py-2.5 text-center">
                      {r.status === 'new' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium border border-green-200">New</span>
                      )}
                      {r.status === 'update' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium border border-blue-200">Update</span>
                      )}
                      {r.status === 'no-change' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">No change</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting || changedRows.length === 0}
            className="px-5 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60 transition-colors"
          >
            {submitting
              ? 'Saving…'
              : changedRows.length === 0
              ? 'No changes to save'
              : `Confirm ${changedRows.length} change${changedRows.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Phase Edit Modal ─────────────────────────────────────────────────────────

function PhaseEditModal({ open, phase, batchId, studentId, existingRecord, onClose, onSaved }) {
  const [marks, setMarks] = useState('');
  const [examAttemptDatetime, setExamAttemptDatetime] = useState('');
  const [attempt, setAttempt] = useState(ATTEMPT_OPTIONS[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (existingRecord) {
      setMarks(existingRecord.marks != null ? String(existingRecord.marks) : '');
      setExamAttemptDatetime(formatDatetimeLocal(existingRecord.exam_attempt_datetime));
      setAttempt(ATTEMPT_OPTIONS.find((o) => o.value === existingRecord.attempt) || ATTEMPT_OPTIONS[0]);
    } else {
      setMarks('');
      setExamAttemptDatetime(formatDatetimeLocal(new Date()));
      setAttempt(ATTEMPT_OPTIONS[0]);
    }
    setError('');
  }, [open, existingRecord]);

  if (!open || !phase) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const payload = {
      batch_id: batchId,
      student_id: studentId,
      diploma_phase_id: phase.id,
      marks: marks !== '' ? marks : null,
      exam_attempt_datetime: examAttemptDatetime || null,
      attempt: attempt.value,
    };
    try {
      let resp;
      if (existingRecord) {
        resp = await axiosInstance.put(`${API}/batch-phase/${existingRecord.id}`, payload);
      } else {
        resp = await axiosInstance.post(`${API}/batch-phase`, payload);
      }
      onSaved(resp.data.batchPhase);
      Swal.fire({ icon: 'success', title: 'Saved!', timer: 1500, showConfirmButton: false });
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!existingRecord) return;
    const result = await Swal.fire({
      title: 'Clear phase result?',
      text: `This will remove the result for "${phase.phase_name}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Yes, clear it',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`${API}/batch-phase/${existingRecord.id}`);
      onSaved(null, existingRecord.id);
      Swal.fire({ icon: 'success', title: 'Cleared', timer: 1500, showConfirmButton: false });
      onClose();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-800">
            {existingRecord ? 'Edit' : 'Add'} Phase Result
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Phase name banner */}
          <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2 text-sm font-medium text-indigo-700">
            {phase.phase_name}
          </div>

          {/* Marks */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Marks</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={marks}
              onChange={(e) => setMarks(e.target.value)}
              placeholder="e.g. 85.50"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          {/* Exam attempt datetime */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Exam Attempted Date & Time</label>
            <input
              type="datetime-local"
              value={examAttemptDatetime}
              onChange={(e) => setExamAttemptDatetime(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          {/* Attempt */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Attempt</label>
            <Select
              options={ATTEMPT_OPTIONS}
              value={attempt}
              onChange={setAttempt}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              classNamePrefix="react-select"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-1">
            {existingRecord ? (
              <button
                type="button"
                onClick={handleDelete}
                className="text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Clear result
              </button>
            ) : <span />}
            <div className="flex gap-2 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60 transition-colors"
              >
                {submitting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function DiplomaResults() {
  // All suitable batches (loaded once on mount)
  const [allBatches, setAllBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);

  // Table data
  const [phases, setPhases] = useState([]);
  const [students, setStudents] = useState([]);
  const [batchPhases, setBatchPhases] = useState([]);

  // Loading / error
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState('');

  // Phase edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editPhase, setEditPhase] = useState(null);
  const [editStudent, setEditStudent] = useState(null);
  const [editExisting, setEditExisting] = useState(null);

  // Search
  const [search, setSearch] = useState('');

  // Phase visibility filter — null means "all"
  const [selectedPhaseIds, setSelectedPhaseIds] = useState(null);

  // Excel upload preview
  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [uploadPreviewRows, setUploadPreviewRows] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // ── Load all suitable batches on mount ────────────────────────────────────

  const loadBatches = useCallback(async () => {
    setLoadingBatches(true);
    setError('');
    try {
      const resp = await axiosInstance.get(`${API}/suitable-batches`);
      setAllBatches(resp.data?.batches || []);
    } catch {
      setError('Failed to load batches');
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  // ── Load batch data when batch changes ────────────────────────────────────

  useEffect(() => {
    if (!selectedBatch) {
      setPhases([]);
      setStudents([]);
      setBatchPhases([]);
      return;
    }
    const fetch = async () => {
      setLoadingData(true);
      setError('');
      try {
        const resp = await axiosInstance.get(
          `${API}/batch-data/${selectedBatch.batchId}/${selectedBatch.diplomaCourseId}`
        );
        setPhases(resp.data?.phases || []);
        setStudents(resp.data?.students || []);
        setBatchPhases(resp.data?.batchPhases || []);
        setSelectedPhaseIds(null); // reset to "all" when batch changes
      } catch {
        setError('Failed to load batch data');
      } finally {
        setLoadingData(false);
      }
    };
    fetch();
  }, [selectedBatch]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  // Group batches by course for react-select grouped options
  const batchOptions = (() => {
    const groups = {};
    allBatches.forEach((b) => {
      const key = b.courseName || 'Unknown';
      if (!groups[key]) groups[key] = [];
      groups[key].push({
        value: b.batchId,
        label: b.batchName || b.batchInternalName || `Batch ${b.batchId}`,
        batchId: b.batchId,
        diplomaCourseId: b.diplomaCourseId,
        status: b.status,
        studentCount: b.studentCount,
        courseName: b.courseName,
        courseShortName: b.courseShortName,
      });
    });
    return Object.entries(groups).map(([label, options]) => ({ label, options }));
  })();

  // Map of `${diploma_phase_id}_${student_id}` → batch-phase records
  const batchPhaseMap = {};
  batchPhases.forEach((bp) => {
    const key = `${bp.diploma_phase_id}_${bp.student_id}`;
    if (!batchPhaseMap[key]) batchPhaseMap[key] = [];
    batchPhaseMap[key].push(bp);
  });

  // For each phase+student, get the most recent record (highest id)
  const latestBatchPhase = (phaseId, studentId) => {
    const key = `${phaseId}_${studentId}`;
    const records = batchPhaseMap[key];
    if (!records || records.length === 0) return null;
    return records.reduce((prev, curr) => (curr.id > prev.id ? curr : prev));
  };

  // Phases currently visible in the table
  const visiblePhases = selectedPhaseIds === null
    ? phases
    : phases.filter((p) => selectedPhaseIds.includes(p.id));

  // Phase filter toggle
  const togglePhase = (phaseId) => {
    if (selectedPhaseIds === null) {
      // Was "all" → now deselect just this one
      const remaining = phases.map((p) => p.id).filter((id) => id !== phaseId);
      setSelectedPhaseIds(remaining.length === 0 ? null : remaining);
    } else if (selectedPhaseIds.includes(phaseId)) {
      const next = selectedPhaseIds.filter((id) => id !== phaseId);
      setSelectedPhaseIds(next.length === 0 ? [phaseId] : next); // prevent empty
    } else {
      const next = [...selectedPhaseIds, phaseId];
      setSelectedPhaseIds(next.length === phases.length ? null : next); // back to "all" if all selected
    }
  };

  const filteredStudents = search.trim()
    ? students.filter((s) => {
        const term = search.toLowerCase();
        return (
          String(s.fullName || '').toLowerCase().includes(term) ||
          String(s.studentId || '').toLowerCase().includes(term) ||
          String(s.registrationNumber || '').toLowerCase().includes(term)
        );
      })
    : students;

  // ── Phase cell click ───────────────────────────────────────────────────────

  const openPhaseEdit = (phase, student) => {
    setEditPhase(phase);
    setEditStudent(student);
    setEditExisting(latestBatchPhase(phase.id, student.studentId));
    setEditModalOpen(true);
  };

  // ── Excel download ───────────────────────────────────────────────────────────

  const downloadExcel = () => {
    const wb = XLSX.utils.book_new();

    // Row 0: machine keys (used by upload parser — do not edit)
    const keyRow = ['student_id', 'reg_no', 'name'];
    phases.forEach((p) => {
      keyRow.push(`marks_${p.id}`, `attempt_${p.id}`, `datetime_${p.id}`);
    });

    // Row 1: human-readable labels
    const labelRow = ['Student ID', 'Reg No.', 'Student Name'];
    phases.forEach((p) => {
      labelRow.push(`${p.phase_name} - Marks`, `${p.phase_name} - Attempt`, `${p.phase_name} - Date & Time`);
    });

    // Row 2+: student data with current marks pre-filled
    const dataRows = students.map((s) => {
      const row = [s.studentId, s.registrationNumber || '', s.fullName];
      phases.forEach((p) => {
        const rec = latestBatchPhase(p.id, s.studentId);
        row.push(
          rec?.marks != null ? Number(rec.marks) : '',
          rec?.attempt || '',
          rec?.exam_attempt_datetime ? formatDatetimeFriendly(rec.exam_attempt_datetime) : '',
        );
      });
      return row;
    });

    const wsData = [keyRow, labelRow, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Column widths
    const wscols = [{ wch: 14 }, { wch: 16 }, { wch: 30 }];
    phases.forEach(() => wscols.push({ wch: 14 }, { wch: 16 }, { wch: 22 }));
    ws['!cols'] = wscols;

    // Freeze top 2 rows + first 3 columns
    ws['!freeze'] = { xSplit: 3, ySplit: 2 };

    XLSX.utils.book_append_sheet(wb, ws, 'Results');

    // Instructions sheet
    const instrRows = [
      ['DIPLOMA RESULTS — UPLOAD INSTRUCTIONS'],
      [''],
      ['HOW TO USE:'],
      ['1. Do NOT modify Row 1 (the machine key row) or columns A, B, C (Student ID, Reg No, Name).'],
      ['2. Fill in marks (numbers), attempt, and exam date-time from Row 3 downwards.'],
      ['3. Save this file and upload it back using the "Upload Excel" button.'],
      ['4. A preview will appear before anything is saved to the system.'],
      [''],
      ['VALID ATTEMPT VALUES — copy exactly:'],
      ['  1st attempt'],
      ['  2nd attempt'],
      ['  3rd attempt'],
      [''],
      ['DATE & TIME FORMAT:  DD/MM/YYYY, HH:MM AM/PM  —  e.g.  27/04/2026, 10:30 am'],
      ['  (The date is pre-filled from the system. You may leave it as-is or clear the cell.)'],
      [''],
      ['PHASES IN THIS BATCH:'],
      ...phases.map((p, i) => [`  ${i + 1}.  ${p.phase_name}   (column key ID: ${p.id})`]),
    ];
    const wsInstr = XLSX.utils.aoa_to_sheet(instrRows);
    wsInstr['!cols'] = [{ wch: 80 }];
    XLSX.utils.book_append_sheet(wb, wsInstr, 'Instructions');

    const batchLabel = (selectedBatchOption?.label || String(selectedBatch.batchId)).replace(/[^a-zA-Z0-9]/g, '_');
    XLSX.writeFile(wb, `diploma_results_${batchLabel}.xlsx`);
  };

  // ── Excel upload parse ───────────────────────────────────────────────────────

  const normalizeAttempt = (val) => {
    const v = String(val || '').trim().toLowerCase();
    if (v.includes('2')) return '2nd attempt';
    if (v.includes('3')) return '3rd attempt';
    return '1st attempt';
  };

  // Parses both friendly ("27 Apr 2026, 01:07 am") and ISO ("2026-04-27T01:07") strings
  // Returns an ISO datetime string ("YYYY-MM-DDTHH:MM") suitable for the backend, or null
  const parseDatetimeForUpload = (raw) => {
    const str = String(raw || '').trim();
    if (!str) return null;
    const d = new Date(str);
    if (!Number.isNaN(d.getTime())) return formatDatetimeLocal(d);
    return null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-uploading the same file

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (raw.length < 3) {
          Swal.fire('Error', 'The uploaded file does not have enough rows. Please use the downloaded template.', 'error');
          return;
        }

        const keyRow = raw[0].map((k) => String(k).trim());
        // raw[1] is the label row — skip it
        const dataRows = raw.slice(2);

        const sidIdx = keyRow.indexOf('student_id');
        if (sidIdx === -1) {
          Swal.fire('Error', 'Invalid file format. Please use the template downloaded from this page.', 'error');
          return;
        }

        // Build phaseId → { marksIdx, attemptIdx, datetimeIdx } map
        const phaseColMap = {};
        keyRow.forEach((key, idx) => {
          let m;
          // eslint-disable-next-line no-cond-assign
          if ((m = key.match(/^marks_(\d+)$/))) {
            const pid = Number(m[1]);
            phaseColMap[pid] = phaseColMap[pid] || {};
            phaseColMap[pid].marksIdx = idx;
          } else if ((m = key.match(/^attempt_(\d+)$/))) {
            const pid = Number(m[1]);
            phaseColMap[pid] = phaseColMap[pid] || {};
            phaseColMap[pid].attemptIdx = idx;
          } else if ((m = key.match(/^datetime_(\d+)$/))) {
            const pid = Number(m[1]);
            phaseColMap[pid] = phaseColMap[pid] || {};
            phaseColMap[pid].datetimeIdx = idx;
          }
        });

        const previewRows = [];

        dataRows.forEach((row) => {
          const studentId = String(row[sidIdx] || '').trim();
          if (!studentId) return;

          const student = students.find((s) => s.studentId === studentId);
          if (!student) return; // skip unknown students

          phases.forEach((phase) => {
            const cols = phaseColMap[phase.id];
            if (!cols) return;

            const rawMarks = cols.marksIdx != null ? row[cols.marksIdx] : '';
            const rawAttempt = cols.attemptIdx != null ? row[cols.attemptIdx] : '';
            const rawDatetime = cols.datetimeIdx != null ? row[cols.datetimeIdx] : '';

            const newMarksStr = String(rawMarks).trim();
            const newMarks = newMarksStr !== '' && !Number.isNaN(Number(newMarksStr)) ? Number(newMarksStr) : null;
            const newAttempt = String(rawAttempt).trim() !== '' ? normalizeAttempt(rawAttempt) : '1st attempt';
            const newDatetime = parseDatetimeForUpload(rawDatetime);

            // Skip rows where no data was filled in at all
            if (newMarks === null && String(rawAttempt).trim() === '' && !newDatetime) return;

            const currentRecord = latestBatchPhase(phase.id, studentId);
            let status;
            if (!currentRecord) {
              status = 'new';
            } else {
              const curMarks = currentRecord.marks != null ? Number(currentRecord.marks) : null;
              const curAttempt = currentRecord.attempt || '1st attempt';
              const curDatetime = currentRecord.exam_attempt_datetime
                ? formatDatetimeLocal(currentRecord.exam_attempt_datetime)
                : null;
              status =
                curMarks !== newMarks || curAttempt !== newAttempt || curDatetime !== newDatetime
                  ? 'update'
                  : 'no-change';
            }

            previewRows.push({
              studentId,
              studentName: student.fullName,
              phaseId: phase.id,
              phaseName: phase.phase_name,
              newMarks,
              newAttempt,
              newDatetime,
              currentMarks: currentRecord?.marks != null ? Number(currentRecord.marks) : null,
              currentAttempt: currentRecord?.attempt || null,
              status,
            });
          });
        });

        setUploadPreviewRows(previewRows);
        setUploadPreviewOpen(true);
      } catch (err) {
        console.error('Excel parse error:', err);
        Swal.fire('Error', 'Failed to parse the uploaded file.', 'error');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // ── Bulk confirm save ────────────────────────────────────────────────────────────

  const handleBulkConfirm = async () => {
    const toSave = uploadPreviewRows.filter((r) => r.status !== 'no-change');
    if (toSave.length === 0) return;
    setBulkSubmitting(true);
    try {
      await axiosInstance.post(`${API}/bulk-upsert`, {
        batch_id: selectedBatch.batchId,
        rows: toSave.map((r) => ({
          student_id: r.studentId,
          diploma_phase_id: r.phaseId,
          marks: r.newMarks,
          attempt: r.newAttempt,
          exam_attempt_datetime: r.newDatetime || null,
        })),
      });
      // Reload fresh batch data
      const resp = await axiosInstance.get(
        `${API}/batch-data/${selectedBatch.batchId}/${selectedBatch.diplomaCourseId}`
      );
      setBatchPhases(resp.data?.batchPhases || []);
      Swal.fire({
        icon: 'success',
        title: 'Saved!',
        text: `${toSave.length} record${toSave.length !== 1 ? 's' : ''} updated successfully.`,
        timer: 2000,
        showConfirmButton: false,
      });
      setUploadPreviewOpen(false);
      setUploadPreviewRows([]);
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save records', 'error');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleSaved = (updatedRecord, deletedId) => {
    if (deletedId) {
      setBatchPhases((prev) => prev.filter((bp) => bp.id !== deletedId));
    } else if (updatedRecord) {
      setBatchPhases((prev) => {
        const idx = prev.findIndex((bp) => bp.id === updatedRecord.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = updatedRecord;
          return next;
        }
        return [...prev, updatedRecord];
      });
    }
  };

  const selectedBatchOption = selectedBatch
    ? batchOptions.flatMap((g) => g.options).find((o) => o.batchId === selectedBatch.batchId) || null
    : null;

  // ── Recent Entries ─────────────────────────────────────────────────────────

  const openRecentEntriesTab = (entries, limit) => {
    const generatedAt = new Date().toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });

    const attemptBadge = (attempt) => {
      if (attempt === '2nd attempt') return `<span class="badge badge-amber">2nd</span>`;
      if (attempt === '3rd attempt') return `<span class="badge badge-red">3rd</span>`;
      return `<span class="badge badge-green">1st</span>`;
    };

    const fmtDatetime = (val) => {
      if (!val) return '—';
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return val;
      return d.toLocaleString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    };

    const rows = entries.map((e, i) => {
      const batchLabel = e.batchInternalName || e.batchName || `Batch ${e.batch_id}`;
      const courseLabel = e.courseShortName ? `${e.courseName} (${e.courseShortName})` : (e.courseName || '—');
      const marksVal = e.marks != null ? `<strong>${Number(e.marks)}</strong>` : '<span class="dim">—</span>';
      return `
        <tr>
          <td class="sno">${i + 1}</td>
          <td class="sid">${e.student_id || '—'}</td>
          <td class="reg">${e.registrationNumber || '—'}</td>
          <td class="name">${e.studentName || '—'}</td>
          <td class="course">${courseLabel}</td>
          <td class="batch">${batchLabel}</td>
          <td class="phase">${e.phase_name || '—'}</td>
          <td class="marks">${marksVal}</td>
          <td class="att">${attemptBadge(e.attempt)}</td>
          <td class="dt">${
            e.exam_attempt_datetime
              ? fmtDatetime(e.exam_attempt_datetime)
              : `<span class="not-set">${fmtDatetime(e.created_at)}</span>`
          }</td>
          <td class="dt updated">${fmtDatetime(e.updated_at)}</td>
        </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Recent Diploma Result Entries</title>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"></script>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 13px; background: #f3f4f6; color: #111; }
    .page { max-width: 1400px; margin: 0 auto; padding: 24px 20px; }

    /* Header */
    .hdr { background: #fff; border-radius: 12px; padding: 20px 24px; margin-bottom: 16px;
           box-shadow: 0 1px 4px rgba(0,0,0,.08); display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
    .hdr h1 { font-size: 18px; font-weight: 700; color: #1e1b4b; }
    .hdr .meta { font-size: 12px; color: #6b7280; }
    .hdr .meta b { color: #374151; }

    /* Table card */
    .card { background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,.08); }
    .card-info { padding: 14px 20px; border-bottom: 1px solid #f0f0f0; font-size: 12px; color: #6b7280; }
    .card-info b { color: #374151; }

    /* Table */
    .wrap { overflow-x: auto; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    thead tr { background: #1e1b4b; color: #fff; }
    th { padding: 10px 12px; text-align: left; font-size: 11px; font-weight: 600; letter-spacing: .04em; white-space: nowrap; }
    td { padding: 9px 12px; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover td { background: #f9fafb; }
    tbody tr:nth-child(even) td { background: #fafafa; }
    tbody tr:nth-child(even):hover td { background: #f3f4f6; }

    /* Column widths */
    .sno  { text-align: center; width: 36px; color: #9ca3af; font-size: 11px; }
    .sid  { font-family: monospace; font-size: 11px; white-space: nowrap; color: #374151; }
    .reg  { font-family: monospace; font-size: 11px; white-space: nowrap; color: #374151; }
    .name { font-weight: 600; color: #111827; white-space: nowrap; }
    .course { font-size: 11px; color: #4b5563; white-space: nowrap; }
    .batch  { font-size: 11px; color: #374151; white-space: nowrap; }
    .phase  { font-size: 11px; color: #374151; white-space: nowrap; }
    .marks  { text-align: center; font-size: 13px; }
    .att    { text-align: center; }
    .dt     { font-size: 11px; color: #6b7280; white-space: nowrap; }
    .updated { color: #4f46e5; font-weight: 600; }
    .dim { color: #d1d5db; }
    .not-set { color: #9ca3af; font-style: italic; }

    /* Attempt badges */
    .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; border: 1px solid; }
    .badge-green { background: #dcfce7; color: #166534; border-color: #bbf7d0; }
    .badge-amber { background: #fef9c3; color: #854d0e; border-color: #fde68a; }
    .badge-red   { background: #fee2e2; color: #991b1b; border-color: #fecaca; }

    /* Empty */
    .empty { padding: 48px; text-align: center; color: #9ca3af; font-size: 14px; }

    /* Download button */
    .dl-btn { display: inline-flex; align-items: center; gap: 6px; background: #059669; color: #fff; border: none; border-radius: 8px; padding: 7px 16px; font-size: 12px; font-weight: 600; cursor: pointer; white-space: nowrap; }
    .dl-btn:hover { background: #047857; }

    /* Footer */
    .footer { margin-top: 16px; text-align: right; font-size: 11px; color: #9ca3af; }
  </style>
</head>
<body>
  <div class="page">
    <div class="hdr">
      <div>
        <h1>🕐 Recent Diploma Result Entries</h1>
        <div class="meta" style="margin-top:4px">Showing last <b>${entries.length}</b> of requested <b>${limit}</b> &nbsp;·&nbsp; Sorted by most recently updated</div>
      </div>
      <div style="display:flex;align-items:center;gap:14px;flex-wrap:wrap">
        <button onclick="downloadExcel()" class="dl-btn">&#11015; Download Excel</button>
        <div class="meta">Generated: <b>${generatedAt}</b></div>
      </div>
    </div>

    <div class="card">
      <div class="card-info">
        <b>${entries.length}</b> record${entries.length !== 1 ? 's' : ''} found across your coordinate course batches.
        Rows are ordered by <b>last updated</b> time (most recent first).
      </div>
      <div class="wrap">
        ${entries.length === 0
          ? '<div class="empty">No phase result records found for your coordinate course batches.</div>'
          : `<table>
              <thead>
                <tr>
                  <th class="sno">#</th>
                  <th>Student ID</th>
                  <th>Reg No.</th>
                  <th>Student Name</th>
                  <th>Course</th>
                  <th>Batch</th>
                  <th>Phase</th>
                  <th style="text-align:center">Marks</th>
                  <th style="text-align:center">Attempt</th>
                  <th>Exam Attempt Date &amp; Time</th>
                  <th>Last Updated</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
             </table>`
        }
      </div>
    </div>

    <div class="footer">Diploma Results &nbsp;·&nbsp; Kaaryaalaya</div>
  </div>

  <script>
    var DATA = ${JSON.stringify(entries).replace(/<\/script>/gi, '<\\/script>')};
    function downloadExcel() {
      if (typeof XLSX === 'undefined') { alert('Excel library not loaded. Please check your internet connection.'); return; }
      function fmt(val) {
        if (!val) return '';
        var d = new Date(val);
        if (isNaN(d.getTime())) return String(val);
        return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
      }
      var headers = ['#', 'Student ID', 'Reg No.', 'Student Name', 'Course', 'Batch', 'Phase', 'Marks', 'Attempt', 'Exam Attempt Date & Time', 'Last Updated'];
      var dataRows = DATA.map(function(e, i) {
        var batchLabel = e.batchInternalName || e.batchName || ('Batch ' + e.batch_id);
        var courseLabel = e.courseShortName ? (e.courseName + ' (' + e.courseShortName + ')') : (e.courseName || '');
        var examDt = e.exam_attempt_datetime ? fmt(e.exam_attempt_datetime) : fmt(e.created_at);
        return [i + 1, e.student_id || '', e.registrationNumber || '', e.studentName || '', courseLabel, batchLabel, e.phase_name || '', e.marks != null ? Number(e.marks) : '', e.attempt || '', examDt, fmt(e.updated_at)];
      });
      var ws = XLSX.utils.aoa_to_sheet([headers].concat(dataRows));
      ws['!cols'] = [{wch:5},{wch:14},{wch:16},{wch:30},{wch:30},{wch:18},{wch:20},{wch:8},{wch:14},{wch:24},{wch:24}];
      var wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Recent Entries');
      XLSX.writeFile(wb, 'diploma_recent_entries.xlsx');
    }
  </script>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  const handleRecentEntries = async () => {
    const { value: limitStr, isConfirmed } = await Swal.fire({
      title: 'Recent Entries',
      html: '<p style="color:#6b7280;font-size:14px;margin-bottom:4px">How many last entered rows do you want to view?</p>',
      input: 'number',
      inputValue: 50,
      inputAttributes: { min: 1, max: 500, step: 1, placeholder: 'e.g. 50' },
      showCancelButton: true,
      confirmButtonText: 'View in New Tab',
      confirmButtonColor: '#4f46e5',
      inputValidator: (val) => {
        const n = parseInt(val, 10);
        if (!val || !Number.isInteger(n) || n < 1) return 'Please enter a number (min 1)';
        if (n > 500) return 'Maximum is 500';
        return null;
      },
    });
    if (!isConfirmed) return;

    const limit = parseInt(limitStr, 10);
    try {
      const resp = await axiosInstance.get(`${API}/recent-entries`, { params: { limit } });
      openRecentEntriesTab(resp.data?.entries || [], limit);
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to load recent entries', 'error');
    }
  };

  // ── Attempt colour helper ──────────────────────────────────────────────────

  const attemptColors = (attempt) => {
    if (attempt === '2nd attempt') return { badge: 'bg-amber-100 text-amber-800 border border-amber-200', label: 'text-amber-600', dot: 'bg-amber-400' };
    if (attempt === '3rd attempt') return { badge: 'bg-red-100 text-red-800 border border-red-200', label: 'text-red-500', dot: 'bg-red-400' };
    return { badge: 'bg-green-100 text-green-800 border border-green-200', label: 'text-green-600', dot: 'bg-green-400' };
  };

  // ── Print sheet ─────────────────────────────────────────────────────────────

  const printSheet = (type) => {
    // type: 'attendance' | 'marks'
    const batchLabel = selectedBatchOption?.label || `Batch ${selectedBatch.batchId}`;
    const courseName = selectedBatchOption?.courseName || '';
    const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const printedAt = new Date().toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });

    const isAttendance = type === 'attendance';
    const title = isAttendance
      ? 'Diploma Examination — Attendance Sheet'
      : 'Diploma Examination — Marks Sheet';

    // Build table columns: fixed student columns + one col per visible phase
    const phaseHeaders = visiblePhases.map((p) => `<th>${p.phase_name}</th>`).join('');

    const rows = students.map((s, idx) => {
      const phaseCells = visiblePhases.map((p) => {
        if (isAttendance) {
          return `<td class="sig-cell"></td>`;
        }
        const rec = latestBatchPhase(p.id, s.studentId);
        const marksVal = rec?.marks != null ? Number(rec.marks) : '';
        const attemptVal = rec?.attempt
          ? rec.attempt.replace('1st attempt', '1st').replace('2nd attempt', '2nd').replace('3rd attempt', '3rd')
          : '';
        return `<td class="marks-cell">${marksVal !== '' ? `<span class="marks">${marksVal}</span>${attemptVal ? `<span class="att">${attemptVal}</span>` : ''}` : ''}</td>`;
      }).join('');

      return `
        <tr>
          <td class="sno">${idx + 1}</td>
          <td class="sid">${s.studentId}</td>
          <td class="reg">${s.registrationNumber || '—'}</td>
          <td class="name">${s.fullName}</td>
          ${phaseCells}
          ${isAttendance ? '<td class="sig-cell"></td>' : ''}
        </tr>`;
    }).join('');

    const signatureColHeader = isAttendance ? '<th>Signature</th>' : '';

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>${title}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 12mm 10mm 14mm 10mm;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { width: 100%; height: 100%; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 9.5px;
      color: #111;
      background: #fff;
    }

    /* ── Page wrapper ── */
    .page {
      width: 190mm;          /* 210mm (A4 portrait) - 10mm*2 margins */
      min-height: 257mm;
      padding: 0;
      position: relative;
    }

    /* ── Header ── */
    .header { text-align: center; margin-bottom: 10px; }
    .header .org {
      font-size: 13px; font-weight: 700;
      letter-spacing: 0.5px; text-transform: uppercase;
    }
    .header .doc-title {
      font-size: 11.5px; font-weight: 600; margin-top: 3px;
    }
    .header .divider {
      border: none; border-top: 1.5px solid #1e3a5f;
      margin: 6px 0 5px;
    }
    .header .meta {
      font-size: 9.5px; color: #333;
      display: flex; justify-content: center;
      flex-wrap: wrap; gap: 6px 24px;
    }
    .header .meta span b { color: #000; }

    /* ── Table ── */
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 8px;
      table-layout: fixed;
    }
    th, td {
      border: 1px solid #aaa;
      padding: 4px 5px;
      vertical-align: middle;
      overflow: hidden;
    }
    thead tr { background: #1e3a5f !important; color: #fff !important; }
    th {
      font-size: 8.5px; font-weight: 700;
      text-align: center;
      white-space: nowrap;
      padding: 5px 4px;
    }
    tbody tr { height: 22px; }
    tbody tr:nth-child(even) { background: #f0f4fa; }
    .sno  { text-align: center; width: 18px; font-size: 8.5px; }
    .sid  { text-align: center; font-family: monospace; font-size: 8px; width: 56px; }
    .reg  { text-align: center; font-family: monospace; font-size: 8px; width: 60px; }
    .name { text-align: left; font-size: 9px; }
    .sig-cell  { width: 60px; }
    .marks-cell { text-align: center; width: 52px; }
    .marks-cell .marks { display: block; font-weight: 700; font-size: 9.5px; }
    .marks-cell .att   { display: block; font-size: 7.5px; color: #555; margin-top: 1px; }

    /* ── Footer ── */
    .footer {
      margin-top: 16px;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      font-size: 9px;
      color: #444;
    }
    .sig-block { text-align: center; }
    .sig-line  { border-top: 1px solid #555; width: 150px; margin: 24px auto 4px; }
    .printed-note { font-size: 8px; color: #999; text-align: right; }

    /* ── Print ── */
    @media print {
      .no-print { display: none !important; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { width: 100%; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
    }

    /* ── Screen preview ── */
    @media screen {
      body { padding: 16px; background: #d1d5db; }
      .page {
        background: #fff;
        padding: 12mm 10mm;
        width: 210mm;
        margin: 0 auto;
        box-shadow: 0 4px 24px rgba(0,0,0,.22);
      }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div class="org">Diploma Examination Results</div>
      <div class="doc-title">${title}</div>
      <hr class="divider" />
      <div class="meta">
        <span><b>Course:</b> ${courseName}</span>
        <span><b>Batch:</b> ${batchLabel}</span>
        <span><b>Date:</b> ${today}</span>
        <span><b>Total Students:</b> ${students.length}</span>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th class="sno">#</th>
          <th class="sid">Student ID</th>
          <th class="reg">Reg. No.</th>
          <th>Student Name</th>
          ${phaseHeaders}
          ${signatureColHeader}
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>

    <div class="footer">
      <div class="sig-block">
        <div class="sig-line"></div>
        Supervisor Signature
      </div>
      <div class="printed-note">Printed: ${printedAt}</div>
    </div>
  </div>

  <div class="no-print" style="text-align:center; margin-top:16px;">
    <button onclick="window.print()" style="padding:8px 28px;background:#1e3a5f;color:#fff;border:none;border-radius:6px;font-size:13px;cursor:pointer;font-weight:600;">
      🖨 Print
    </button>
  </div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  // ── Status badge ───────────────────────────────────────────────────────────

  const batchStatusBadge = (status) => {
    const map = {
      ongoing: 'bg-green-100 text-green-700 border-green-200',
      'delivery completed': 'bg-blue-100 text-blue-700 border-blue-200',
      'batch completed': 'bg-gray-100 text-gray-600 border-gray-200',
      'not started': 'bg-yellow-50 text-yellow-700 border-yellow-200',
    };
    const cls = map[String(status || '').toLowerCase()] || 'bg-slate-100 text-slate-600 border-slate-200';
    return (
      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${cls}`}>
        {status || '—'}
      </span>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 space-y-6">
        {/* Page header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Diploma Results</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Select a batch to view and manage phase results.
            </p>
          </div>
          <button
            type="button"
            onClick={handleRecentEntries}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-violet-50 border border-violet-200 text-violet-700 hover:bg-violet-100 transition-colors whitespace-nowrap flex-shrink-0 mt-1"
          >
            🕐 Recent Entries
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* Batch selector */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Batch
          </label>
          {loadingBatches ? (
            <div className="h-9 bg-gray-100 rounded-lg animate-pulse" />
          ) : (
            <Select
              options={batchOptions}
              value={selectedBatchOption}
              onChange={(opt) => setSelectedBatch(opt ? { batchId: opt.batchId, diplomaCourseId: opt.diplomaCourseId } : null)}
              placeholder="Select a batch…"
              isClearable
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              noOptionsMessage={() => 'No diploma batches found'}
              formatOptionLabel={(opt) => (
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium text-sm">{opt.label}</div>
                    <div className="text-xs text-gray-400">{opt.courseName}{opt.courseShortName ? ` (${opt.courseShortName})` : ''}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {opt.status && batchStatusBadge(opt.status)}
                    {opt.studentCount != null && (
                      <span className="text-xs text-gray-400">{opt.studentCount} students</span>
                    )}
                  </div>
                </div>
              )}
            />
          )}
        </div>

        {/* Results table */}
        {selectedBatch && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
            {/* Table header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-sm font-semibold text-gray-700">
                  {selectedBatchOption?.courseName} — {selectedBatchOption?.label}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {students.length} student{students.length !== 1 ? 's' : ''} · {phases.length} phase{phases.length !== 1 ? 's' : ''}
                  {selectedPhaseIds !== null && ` (${visiblePhases.length} visible)`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={downloadExcel}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-colors whitespace-nowrap"
                >
                  ↓ Download Excel
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 transition-colors whitespace-nowrap"
                >
                  ↑ Upload Excel
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  onClick={() => printSheet('attendance')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-50 border border-slate-200 text-slate-700 hover:bg-slate-100 transition-colors whitespace-nowrap"
                >
                  🖨 Attendance
                </button>
                <button
                  type="button"
                  onClick={() => printSheet('marks')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-50 border border-slate-200 text-slate-700 hover:bg-slate-100 transition-colors whitespace-nowrap"
                >
                  🖨 Marks Sheet
                </button>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search students…"
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </div>

            {loadingData ? (
              <div className="py-16 text-center text-gray-400 text-sm">Loading batch data…</div>
            ) : phases.length === 0 ? (
              <div className="py-16 text-center">
                <p className="text-gray-400 text-sm">No phases configured for this diploma course.</p>
                <p className="text-gray-400 text-xs mt-1">
                  Add phases in <span className="font-medium">Group Diploma Courses</span> settings.
                </p>
              </div>
            ) : students.length === 0 ? (
              <div className="py-16 text-center text-gray-400 text-sm">
                No active students found in this batch.
              </div>
            ) : (
              <>
                {/* Phase filter bar */}
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide mr-1">Show:</span>
                    {/* All pill */}
                    <button
                      type="button"
                      onClick={() => setSelectedPhaseIds(null)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                        selectedPhaseIds === null
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                          : 'bg-white text-gray-500 border-gray-200 hover:border-indigo-300 hover:text-indigo-600'
                      }`}
                    >
                      All phases
                    </button>
                    {/* Individual phase pills */}
                    {phases.map((phase) => {
                      const active = selectedPhaseIds === null || selectedPhaseIds.includes(phase.id);
                      return (
                        <button
                          key={phase.id}
                          type="button"
                          onClick={() => togglePhase(phase.id)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                            active
                              ? 'bg-white text-indigo-700 border-indigo-300 shadow-sm'
                              : 'bg-gray-100 text-gray-400 border-gray-200 line-through hover:border-gray-300 hover:text-gray-500'
                          }`}
                        >
                          {phase.phase_name}
                        </button>
                      );
                    })}
                    {selectedPhaseIds !== null && (
                      <span className="text-xs text-gray-400 ml-1">
                        {visiblePhases.length} of {phases.length} shown
                      </span>
                    )}
                  </div>
                </div>

                {/* Students table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">
                          #
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                          Student ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                          Registration
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Name
                        </th>
                        {visiblePhases.map((phase) => (
                          <th
                            key={phase.id}
                            className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                          >
                            {phase.phase_name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredStudents.map((student, idx) => (
                        <tr key={student.studentId} className="hover:bg-gray-50/60 transition-colors">
                          <td className="px-4 py-3 text-gray-400 text-xs">{idx + 1}</td>
                          <td className="px-4 py-3 text-gray-600 font-mono text-xs whitespace-nowrap">
                            {student.studentId}
                          </td>
                          <td className="px-4 py-3 text-gray-600 font-mono text-xs whitespace-nowrap">
                            {student.registrationNumber || '—'}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                            {student.fullName}
                          </td>
                          {visiblePhases.map((phase) => {
                            const record = latestBatchPhase(phase.id, student.studentId);
                            return (
                              <td
                                key={phase.id}
                                className="px-4 py-3 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                                title={`Edit ${phase.phase_name}`}
                                onClick={() => openPhaseEdit(phase, student)}
                              >
                                {record ? (() => {
                                  const ac = attemptColors(record.attempt);
                                  const attemptLabel = record.attempt === '2nd attempt' ? '2nd' : record.attempt === '3rd attempt' ? '3rd' : '1st';
                                  return (
                                    <div className="flex flex-col items-center gap-0.5">
                                      {record.marks != null ? (
                                        <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${ac.badge}`}>
                                          {record.marks}
                                        </span>
                                      ) : (
                                        <span className="text-gray-300 text-xs">—</span>
                                      )}
                                      <span className={`text-xs font-medium ${ac.label}`}>{attemptLabel}</span>
                                      {record.exam_attempt_datetime ? (
                                        <div className="text-emerald-500 text-xs" title="Exam completed">✓</div>
                                      ) : null}
                                    </div>
                                  );
                                })() : (
                                  <span className="text-gray-200 text-xs">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {filteredStudents.length === 0 && search && (
                  <div className="py-8 text-center text-gray-400 text-sm">
                    No students match &quot;{search}&quot;
                  </div>
                )}

                {/* Phase legend */}
                <div className="px-5 py-3 border-t border-gray-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-400" />
                    1st attempt
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
                    2nd attempt
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-400" />
                    3rd attempt
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-emerald-500">✓</span> Exam completed
                  </span>
                  <span className="ml-auto italic">Click any cell to edit</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Empty state when nothing selected */}
        {!selectedBatch && !loadingData && (
          <div className="bg-white rounded-xl border border-dashed border-gray-200 py-16 text-center">
            <div className="text-4xl mb-3">🎓</div>
            <p className="text-gray-500 font-medium">Select a coordinate course and batch above</p>
            <p className="text-gray-400 text-sm mt-1">
              Diploma phase results will appear here
            </p>
          </div>
        )}
      </div>

      {/* Phase edit modal */}
      <PhaseEditModal
        open={editModalOpen}
        phase={editPhase}
        batchId={selectedBatch?.batchId}
        studentId={editStudent?.studentId}
        existingRecord={editExisting}
        onClose={() => setEditModalOpen(false)}
        onSaved={handleSaved}
      />

      <ExcelUploadPreviewModal
        open={uploadPreviewOpen}
        rows={uploadPreviewRows}
        onClose={() => { setUploadPreviewOpen(false); setUploadPreviewRows([]); }}
        onConfirm={handleBulkConfirm}
        submitting={bulkSubmitting}
      />
    </DashboardLayout>
  );
}
